import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
	Comment,
	CommentDTO,
	CommentRequest,
} from '../../lib/interfaces/Comment';
import { commentFields } from '../Form/form.utils';
import Form, { ResetFormHandler } from '../Form/Form';
import { mutationTypes } from './comments.helper';
import { FormField } from '../../lib/interfaces/Form';

type CommentFormProps = {
	initialData?: CommentDTO;
	currentTaskId: number;
	parentComment?: Comment;
	replyComment?: Comment;
	editableComment?: Comment;
	onClose?: () => void;
	mutationFn: (
		data: CommentDTO,
		id: number,
		commentId?: number,
	) => Promise<Comment>;
	refetchComments: () => void;
};

const CommentForm: FC<CommentFormProps> = ({
	initialData = { comment: '' },
	currentTaskId,
	parentComment,
	replyComment,
	onClose,
	mutationFn,
	refetchComments,
	editableComment,
}) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const childRef = useRef<ResetFormHandler>(null);

	const commentFormFields = commentFields.map((field: FormField) => {
		if (field.label) {
			field.label = t(field.label);
		}
		return field;
	});

	const getMutationType = () => {
		if (replyComment) {
			return mutationTypes.REPLY;
		}
		if (editableComment) {
			return mutationTypes.EDIT;
		}
		return mutationTypes.NEW;
	};
	const mutationType: string = getMutationType();

	const commentMutation = useMutation<Comment, Error, CommentRequest>(
		(data) => mutationFn(data.data, data.id, data.commentId),
		/* {
			onMutate: (data) => {
				console.log('data: ', data);
				// For edit optimisitc update.
				if (editableComment) {
					queryClient.setQueryData(`comments-${currentTaskId}`, (old: any) =>
						old?.map((comment: any) => {
							if (parentComment) {
								if (comment.id === parentComment.id) {
									// eslint-disable-next-line no-param-reassign
									comment.children = comment.children.map((child: Comment) => {
										if (child.id === data?.comment?.id) {
											return data?.comment || child;
										}
										return child;
									});
									return comment;
								}
							}
							if (comment.id === data?.comment?.id) {
								return data?.comment || comment;
							}
							return comment;
						}),
					);
				}
			},
			onSuccess(data, variables, context) {
				console.log('data, variables, context: ', data, variables, context);
			},
		}, */
	);

	const handleSubmit = (formValues: Comment) => {
		const args: CommentRequest = { data: formValues, id: currentTaskId };
		if (mutationType === mutationTypes.REPLY) {
			// TODO change CommentRequest to readable interface or params assignment. No tricks with ids.
			args.id = currentTaskId;
			args.commentId = parentComment ? parentComment.id : replyComment?.id;
		} else if (mutationType === mutationTypes.EDIT && editableComment) {
			args.id = editableComment.id;
			args.comment = formValues;
		}
		commentMutation.mutate(args, {
			onSuccess(modifiedComment: Comment) {
				refetchComments();
				queryClient.setQueryData(`comments-${currentTaskId}`, (old: any) => {
					if (mutationType === mutationTypes.EDIT) {
						const modifiedQueryData = old?.map((comment: any) => {
							if (parentComment && comment.id === parentComment.id) {
								// eslint-disable-next-line no-param-reassign
								comment.children = comment.children.map((child: Comment) => {
									if (child.id === modifiedComment.id) {
										return modifiedComment;
									}
									return child;
								});
								return comment;
							}
							if (comment.id === modifiedComment.id) {
								return modifiedComment || comment;
							}
							return comment;
						});
						return modifiedQueryData;
					}
					if (mutationType === mutationTypes.REPLY) {
						const modifiedQueryData = old?.map((comment: any) => {
							if (replyComment && replyComment.id === comment.id) {
								// eslint-disable-next-line no-param-reassign
								comment.children = comment.children || [];
								comment.children.push(modifiedComment);
							}
							if (parentComment && parentComment.id === comment.id) {
								// eslint-disable-next-line no-param-reassign
								comment.children = comment.children || [];
								comment.children.push(modifiedComment);
							}
							return comment;
						});
						return modifiedQueryData;
					}
					if (mutationType === mutationTypes.NEW) {
						old.push(modifiedComment);
						return old;
					}
					return old;
				});
				if (mutationType === mutationTypes.NEW) {
					childRef?.current?.reset();
				} else {
					// eslint-disable-next-line no-unused-expressions
					!!onClose && onClose();
				}
			},
		});
	};

	return (
		<div style={{ marginBottom: '10px', maxWidth: '400px' }}>
			<Form
				fields={commentFormFields}
				onSubmit={handleSubmit}
				initialValues={initialData}
				saveButtonName={t('send')}
				cancelButtonName={t('cancel')}
				onCancel={onClose}
				isLoading={commentMutation.isLoading}
				ref={childRef}
				additionalButtonName={initialData.link ? '' : t('addLink')}
			/>
		</div>
	);
};

export default CommentForm;
